import { create } from "zustand";

export interface PendingEvidence {
  // Client-generated stable id. Used for async-safe removal — the index-based
  // remove path was racy because pending tray state can mutate while an
  // addEvidence mutation is in flight, causing `onSuccess` to splice the
  // wrong row. Always set by `addPending`.
  id: string;
  text: string;
  paperId: string;
  paperTitle: string;
  pageNumber?: number;
  // Present for PDF stashes (a PaperHighlight was created at stash time);
  // absent for HTML stashes (preserves today's store-only behavior).
  highlightId?: string;
  rects?: Array<{ x: number; y: number; width: number; height: number; pageIndex: number }>;
}

interface EvidenceMapState {
  isOpen: boolean;
  pendingEvidence: PendingEvidence[];
  selectedClaimId: string | null;
  toggle: () => void;
  open: () => void;
  close: () => void;
  // `id` is generated if not supplied — callers don't need to provide one.
  addPending: (evidence: Omit<PendingEvidence, "id"> & { id?: string }) => void;
  removePending: (index: number) => void;
  removePendingById: (id: string) => void;
  clearPending: () => void;
  selectClaim: (claimId: string | null) => void;
}

export const useEvidenceStore = create<EvidenceMapState>((set) => ({
  isOpen: false,
  pendingEvidence: [],
  selectedClaimId: null,

  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),

  addPending: (evidence) =>
    set((state) => {
      // Dedup only when the incoming entry has a highlightId (PDF path).
      // HTML stashes omit highlightId and should not dedup.
      if (
        evidence.highlightId &&
        state.pendingEvidence.some((e) => e.highlightId === evidence.highlightId)
      ) {
        return state;
      }
      const id = evidence.id ?? crypto.randomUUID();
      return {
        pendingEvidence: [...state.pendingEvidence, { ...evidence, id }],
      };
    }),

  removePending: (index) =>
    set((state) => ({
      pendingEvidence: state.pendingEvidence.filter((_, i) => i !== index),
    })),

  removePendingById: (id) =>
    set((state) => ({
      pendingEvidence: state.pendingEvidence.filter((e) => e.id !== id),
    })),

  clearPending: () => set({ pendingEvidence: [] }),

  selectClaim: (claimId) => set({ selectedClaimId: claimId }),
}));
